'use client';

import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Toaster as Sonner, toast } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

function useDocumentTheme(): 'light' | 'dark' {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark ? 'dark' : 'light';
}

const Toaster = ({ theme, ...props }: ToasterProps) => {
  const documentTheme = useDocumentTheme();

  return (
    <Sonner
      theme={theme ?? documentTheme}
      // Top avoids covering the live pause/record CTA on mobile (bottom
      // is the primary action strip in LiveConvo). Desktop also reads
      // better above the fold for short system notices.
      position='top-center'
      visibleToasts={3}
      closeButton
      duration={5000}
      className='toaster group'
      containerAriaLabel='Notifications'
      // Keep system toasts clear of notches / home indicators.
      offset={16}
      mobileOffset={16}
      icons={{
        success: (
          <CheckCircle2 className='h-5 w-5 text-emerald-600 dark:text-emerald-400' />
        ),
        info: <Info className='h-5 w-5 text-sky-600 dark:text-sky-400' />,
        warning: (
          <AlertTriangle className='h-5 w-5 text-amber-600 dark:text-amber-400' />
        ),
        error: <XCircle className='h-5 w-5 text-red-600 dark:text-red-400' />,
        loading: (
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
        ),
      }}
      toastOptions={{
        closeButtonAriaLabel: 'Dismiss notification',
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:!bg-background group-[.toast]:!text-foreground group-[.toast]:!border group-[.toast]:!border-border group-[.toast]:!rounded-md group-[.toast]:!px-3 group-[.toast]:!py-1.5 group-[.toast]:!text-sm group-[.toast]:!font-medium',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
